import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 2.0;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 8;

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #isMouseDown = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {number} */
  #lastTouchEndTime = 0;

  /** @type {number} */
  #startScrollLeft = 0;

  get #image() {
    return this.refs.image;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    // Touch events
    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Mouse events for desktop
    this.addEventListener('mousedown', this.#handleMouseDown, { signal });
    window.addEventListener('mousemove', this.#handleMouseMove, { signal });
    window.addEventListener('mouseup', this.#handleMouseUp, { signal });
    this.addEventListener('wheel', this.#handleWheel, options);

    // Initialize transform immediately
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (event.button !== 0) return;
    if (performance.now() - this.#lastTouchEndTime < 500) return;

    event.preventDefault();
    this.#isMouseDown = true;
    this.#hasDraggedBeyondThreshold = false;
    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    const galleryList = this.closest('.dialog-zoomed-gallery');
    this.#startScrollLeft = galleryList ? galleryList.scrollLeft : 0;
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isMouseDown) return;

    const distance = getDistance({ x: event.clientX, y: event.clientY }, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance >= DRAG_THRESHOLD) {
      this.#hasDraggedBeyondThreshold = true;
      this.classList.add('is-dragging');
      if (this.#scale <= MIN_ZOOM) {
        const galleryList = this.closest('.dialog-zoomed-gallery');
        if (galleryList) {
          galleryList.style.scrollBehavior = 'auto';
          galleryList.style.scrollSnapType = 'none';
        }
      }
    }

    if (this.#hasDraggedBeyondThreshold) {
      if (this.#scale > MIN_ZOOM) {
        const dx = event.clientX - this.#startPosition.x;
        const dy = event.clientY - this.#startPosition.y;

        this.#translate.x = this.#startTranslate.x + dx / this.#scale;
        this.#translate.y = this.#startTranslate.y + dy / this.#scale;

        this.#requestUpdateTransform();
      } else {
        const dx = event.clientX - this.#startPosition.x;
        const galleryList = this.closest('.dialog-zoomed-gallery');
        if (galleryList) {
          galleryList.scrollLeft = this.#startScrollLeft - dx;
        }
      }
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseUp = (event) => {
    if (!this.#isMouseDown) return;
    this.#isMouseDown = false;
    this.classList.remove('is-dragging');

    const galleryList = this.closest('.dialog-zoomed-gallery');
    if (galleryList) {
      galleryList.style.scrollBehavior = '';
      galleryList.style.scrollSnapType = '';
    }

    if (!this.#hasDraggedBeyondThreshold) {
      this.#toggleZoomAtPoint(event.clientX, event.clientY);
    }
  };

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    event.preventDefault();
    const delta = -event.deltaY * 0.003;
    const oldScale = this.#scale;
    const newScale = clamp(this.#scale + delta, MIN_ZOOM, MAX_ZOOM);
    if (newScale === oldScale) return;

    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    const distanceFromCenter = {
      x: event.clientX - containerCenter.x,
      y: event.clientY - containerCenter.y,
    };

    this.#scale = newScale;
    const scaleDelta = this.#scale / oldScale - 1.0;
    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (event.touches.length > 1 || this.#scale > MIN_ZOOM) {
      preventDefault(event);
    }

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      if (this.#lastTapPosition) {
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0;
          this.#lastTapPosition = null;
          this.#hasDraggedBeyondThreshold = true;
          return;
        }
      }

      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = true;
  }

  /**
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
    const galleryList = this.closest('.dialog-zoomed-gallery');
    this.#startScrollLeft = galleryList ? galleryList.scrollLeft : 0;
  }

  /**
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * @param {Touch} touch
   */
  #handleDoubleTapFromTouch(touch) {
    this.#toggleZoomAtPoint(touch.clientX, touch.clientY);
  }

  /**
   * Toggle scale zoom at click point
   * @param {number} clientX
   * @param {number} clientY
   */
  #toggleZoomAtPoint(clientX, clientY) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;
    const tolerance = 0.05;

    if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
      targetZoom = DEFAULT_ZOOM;
    } else {
      targetZoom = MIN_ZOOM;
    }

    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);

      const distanceFromCenter = {
        x: clientX - containerCenter.x,
        y: clientY - containerCenter.y,
      };

      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 };
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (event.touches.length > 1 || (this.#scale > MIN_ZOOM && this.#isDragging)) {
      preventDefault(event);
    }

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    this.#hasManualZoom = true;

    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = true;
  }

  /**
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    if (this.#scale > MIN_ZOOM) {
      this.classList.add('is-dragging');

      const dx = touch.clientX - this.#startPosition.x;
      const dy = touch.clientY - this.#startPosition.y;

      this.#translate.x = this.#startTranslate.x + dx / this.#scale;
      this.#translate.y = this.#startTranslate.y + dy / this.#scale;

      this.#requestUpdateTransform();
    }
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    this.#lastTouchEndTime = performance.now();

    const galleryList = this.closest('.dialog-zoomed-gallery');
    if (galleryList) {
      galleryList.style.scrollBehavior = '';
      galleryList.style.scrollSnapType = '';
    }

    if (event.touches.length === 0) {
      if (!this.#hasDraggedBeyondThreshold) {
        const touch = event.changedTouches[0];
        if (touch) {
          this.#toggleZoomAtPoint(touch.clientX, touch.clientY);
        }
      }
      this.#isDragging = false;
      this.classList.remove('is-dragging');
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    const wrapperRect = this.getBoundingClientRect();

    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      naturalWidth = wrapperRect.width || 800;
      naturalHeight = wrapperRect.height || 1000;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let baseImageWidth, baseImageHeight;
    if (imageAspectRatio > wrapperAspectRatio) {
      baseImageWidth = wrapperRect.width;
      baseImageHeight = baseImageWidth / imageAspectRatio;
    } else {
      baseImageHeight = wrapperRect.height;
      baseImageWidth = baseImageHeight * imageAspectRatio;
    }

    const scaledImageWidth = baseImageWidth * this.#scale;
    const scaledImageHeight = baseImageHeight * this.#scale;

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    if (this.#scale > MIN_ZOOM) {
      this.classList.add('is-zoomed');
    } else {
      this.classList.remove('is-zoomed');
      this.classList.remove('is-dragging');
    }
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  #resetZoom = () => {
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;
    this.#translate.x = 0;
    this.#translate.y = 0;

    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#isMouseDown = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#startScrollLeft = 0;

    this.classList.remove('is-zoomed', 'is-dragging');
    this.style.setProperty('--drag-zoom-scale', MIN_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  resetZoom() {
    this.#resetZoom();
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

function getDistance(point1, point2) {
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}
